// src/pages/QuickQuote.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, Truck, Clock, MapPin, ArrowRight, ArrowLeft, 
  Home, Check, User, Palette, AlertTriangle,
  AlertCircle,
  Download
} from 'lucide-react';

// Import jsPDF correctly
import jsPDF from 'jspdf';

// Send the quote by email alongside the PDF download
import emailjs from '@emailjs/browser';
import { track } from '../utils/track';

// Import styled components
import {
  PageWrapper,
  Container,
  Breadcrumb,
  Title,
  Tabs,
  TabButton,
  Card,
  FormGroup,
  Label,
  Required,
  Input,
  Select,
  ButtonGroup,
  Button,
  SuccessScreenWrapper,
  SuccessTitle,
  SuccessMessage,
  ReferenceBox,
  ReferenceCode,
  SectionHeader,
  PricingCard,
  PriceBreakdown,
  PriceRow,
  LoadTypeGrid,
  LoadTypeCard,
  TimeSlotGrid,
  TimeSlot,
  UrgencyGrid,
  UrgencyCard,
  WarningBox,
  ErrorBox,
  InfoBox,
  LoadingSpinner
} from './QuickQuote.styles';

// Import pricing calculator
import { calculatePrice, formatCurrency, HAMPSHIRE_TOWNS, isHampshirePostcode } from '../utils/pricingCalculator';

// Company details used in the generated PDF and quote reference.
// Change these here rather than hunting through the file if they ever update.
const COMPANY_NAME = 'Urgent Logistics';
const COMPANY_PHONE = '07436 117842';
const COMPANY_PHONE_TEL = '+447436117842';
const COMPANY_EMAIL = 'info@urgentservicelogistics.co.uk';
const QUOTE_REF_PREFIX = 'USL';

// Success Screen Component
const SuccessScreen = ({ reference, price, bookingDetails, onDownloadAgain }) => (
  <SuccessScreenWrapper
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
  >
    <Download size={80} style={{ color: '#16a34a', marginBottom: '1.5rem' }} />
    <SuccessTitle>Quote Generated Successfully!</SuccessTitle>
    <SuccessMessage>
      Your detailed quote has been downloaded as a PDF. 
      You can also save the reference number below for future reference.
    </SuccessMessage>
    <div style={{ margin: '2rem 0' }}>
      <ReferenceBox>
        Quote Reference: <ReferenceCode>{reference}</ReferenceCode>
      </ReferenceBox>
      <div style={{ 
        background: '#f0f9ff', 
        padding: '1.5rem', 
        borderRadius: '12px', 
        marginTop: '1rem',
        border: '2px solid #bae6fd'
      }}>
        <h4 style={{ margin: '0 0 0.5rem', color: '#0c4a6e' }}>Quote Amount</h4>
        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#001F3D' }}>
          {formatCurrency(price)}
        </div>
        <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#64748b' }}>
          {bookingDetails?.isLocal ? 'Local Hampshire delivery' : 'UK-wide delivery'}
        </p>
      </div>
    </div>
    <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8fafc', borderRadius: '12px' }}>
      <h4 style={{ margin: '0 0 0.5rem', color: '#475569' }}>What happens next?</h4>
      <ul style={{ margin: '0', paddingLeft: '1.5rem', textAlign: 'left' }}>
        <li>Review your downloaded PDF quote</li>
        <li>Contact us with your reference number to book</li>
        <li>Call us at <a href={`tel:${COMPANY_PHONE_TEL}`} style={{ color: '#001F3D', fontWeight: 600 }}>{COMPANY_PHONE}</a> to discuss your requirements</li>
        <li>All items must be properly secured and palletized</li>
      </ul>
    </div>
    <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
      <Link to="/">
        <Button>
          <Home size={20} /> Back to Home
        </Button>
      </Link>
      <Button 
        $primary 
        onClick={onDownloadAgain}
      >
        <Download size={20} /> Download Again
      </Button>
    </div>
  </SuccessScreenWrapper>
);

// Function to generate PDF (without autoTable dependency)
const generateQuotePDF = (quoteData) => {
  const doc = new jsPDF();
  
  // Set document properties
  doc.setProperties({
    title: `Quote ${quoteData.reference}`,
    subject: 'Delivery Quote',
    author: COMPANY_NAME,
  });
  
  // Add header
  doc.setFontSize(20);
  doc.setTextColor(40, 40, 40);
  doc.text(COMPANY_NAME.toUpperCase(), 105, 20, { align: 'center' });
  
  doc.setFontSize(16);
  doc.setTextColor(255, 107, 53); // Orange color
  doc.text('DELIVERY QUOTE', 105, 30, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Quote Reference: ${quoteData.reference}`, 105, 40, { align: 'center' });
  doc.text(`Date: ${new Date().toLocaleDateString('en-GB')}`, 105, 45, { align: 'center' });
  
  // Add line separator
  doc.setDrawColor(255, 107, 53);
  doc.setLineWidth(0.5);
  doc.line(20, 50, 190, 50);
  
  let yPos = 60;
  
  // Quote Summary
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 40);
  doc.text('QUOTE SUMMARY', 20, yPos);
  yPos += 10;
  
  doc.setFontSize(12);
  doc.setTextColor(255, 107, 53);
  doc.text(`Total Amount: ${formatCurrency(quoteData.price)}`, 20, yPos);
  yPos += 10;
  
  // Service Information
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 40);
  doc.text('SERVICE DETAILS', 20, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  
  // Convert all values to strings
  const serviceDetails = [
    ['Service Type:', quoteData.isLocal ? 'Local Hampshire' : 'UK-Wide Delivery'],
    ['Distance (one way):', `${quoteData.oneWayMiles || 0} miles`],
    ['Distance (round trip):', `${quoteData.roundTripMiles || 0} miles`],
    ['Urgency:', quoteData.urgency || 'Standard'],
    ['Time Slot:', quoteData.timeOfDay || 'Standard hours'],
    ['Weekend Service:', quoteData.isWeekend ? 'Yes' : 'No'],
    ['Bank Holiday:', quoteData.isBankHoliday ? 'Yes' : 'No'],
  ];
  
  serviceDetails.forEach(([label, value]) => {
    doc.text(`${label}`, 20, yPos);
    doc.text(`${value}`, 100, yPos);
    yPos += 7;
  });
  
  yPos += 5;
  
  // Pickup Details
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 40);
  doc.text('PICKUP DETAILS', 20, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  
  // Convert all values to strings and handle empty values
  const pickupDetails = [
    ['Address:', quoteData.pickupAddress?.street || ''],
    ['', quoteData.pickupAddress?.line2 || ''],
    ['', `${quoteData.pickupAddress?.city || ''} ${quoteData.pickupAddress?.postcode || ''}`.trim()],
    ['Contact:', quoteData.sender?.name || ''],
    ['Phone:', quoteData.sender?.phone || ''],
    ['Email:', quoteData.sender?.email || ''],
  ];
  
  pickupDetails.forEach(([label, value]) => {
    // Only add if there's content
    if (value || label) {
      doc.text(`${label}`, 20, yPos);
      if (value) {
        doc.text(`${value}`, 60, yPos);
      }
      yPos += 7;
    }
  });
  
  yPos += 5;
  
  // Delivery Details
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 40);
  doc.text('DELIVERY DETAILS', 20, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  
  // Convert all values to strings
  const deliveryDetails = [
    ['Address:', quoteData.deliveryAddress?.street || ''],
    ['', quoteData.deliveryAddress?.line2 || ''],
    ['', `${quoteData.deliveryAddress?.city || ''} ${quoteData.deliveryAddress?.postcode || ''}`.trim()],
    ['Recipient:', quoteData.recipient?.name || ''],
    ['Phone:', quoteData.recipient?.phone || ''],
  ];
  
  deliveryDetails.forEach(([label, value]) => {
    // Only add if there's content
    if (value || label) {
      doc.text(`${label}`, 20, yPos);
      if (value) {
        doc.text(`${value}`, 60, yPos);
      }
      yPos += 7;
    }
  });
  
  yPos += 5;
  
  // Load Details - Manual table implementation
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 40);
  doc.text('LOAD DETAILS', 20, yPos);
  yPos += 10;
  
  // Convert all values to strings
  const loadDetails = [
    ['Load Type:', quoteData.loadType || 'Not specified'],
    ['Number of Pallets:', `${quoteData.numberOfPallets || 0}`],
    ['Weight:', `${quoteData.weight || 0} kg`],
    ['Declared Value:', quoteData.declaredValue ? `£${quoteData.declaredValue}` : 'Not specified'],
    ['Waiting Time:', `${quoteData.waitingTime || 0} hours`],
  ];
  
  // Draw table header
  doc.setFillColor(255, 107, 53);
  doc.rect(20, yPos, 170, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text('Description', 25, yPos + 6);
  doc.text('Details', 145, yPos + 6, { align: 'right' });
  
  yPos += 8;
  
  // Draw table rows
  loadDetails.forEach(([label, value], index) => {
    // Alternate row colors
    if (index % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(20, yPos, 170, 8, 'F');
    }
    
    doc.setTextColor(40, 40, 40);
    doc.text(label, 25, yPos + 6);
    doc.text(String(value), 165, yPos + 6, { align: 'right' });
    yPos += 8;
  });
  
  yPos += 10;
  
  // Price Breakdown - Manual table implementation
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 40);
  doc.text('PRICE BREAKDOWN', 20, yPos);
  yPos += 10;
  
  const priceRows = [
    ['Base Rate:', formatCurrency(quoteData.basePrice || 0)],
    ['Urgency Surcharge:', formatCurrency(quoteData.breakdown?.urgencySurcharge || 0)],
    ['Time Surcharge:', formatCurrency(quoteData.breakdown?.timeSurcharge || 0)],
    ['Load Surcharge:', formatCurrency(quoteData.breakdown?.loadSurcharge || 0)],
    ['Extras:', formatCurrency(quoteData.extrasTotal || 0)],
    ['TOTAL:', formatCurrency(quoteData.price || 0)],
  ];
  
  // Draw price breakdown table
  priceRows.forEach(([label, value], index) => {
    if (index === priceRows.length - 1) {
      // Highlight total row
      doc.setFillColor(255, 107, 53, 20); // Light orange background
      doc.rect(20, yPos, 170, 10, 'F');
      doc.setDrawColor(255, 107, 53);
      doc.setLineWidth(0.5);
      doc.line(20, yPos, 190, yPos);
      doc.line(20, yPos + 10, 190, yPos + 10);
    }
    
    doc.setTextColor(index === priceRows.length - 1 ? 255 : 40, 
                     index === priceRows.length - 1 ? 107 : 40, 
                     index === priceRows.length - 1 ? 53 : 40);
    
    if (index === priceRows.length - 1) {
      doc.setFont(undefined, 'bold');
    }
    
    doc.text(label, 25, yPos + 7);
    doc.text(value, 165, yPos + 7, { align: 'right' });
    
    if (index === priceRows.length - 1) {
      doc.setFont(undefined, 'normal');
    }
    
    yPos += 10;
  });
  
  yPos += 15;
  
  // Terms and Conditions
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text('TERMS & CONDITIONS', 20, yPos);
  yPos += 7;
  
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  
  const terms = [
    '1. Maximum payload: 1,500kg (1.5 tonnes)',
    '2. All loads must be properly secured and palletized',
    '3. First 10 minutes waiting time free, then £20/hour',
    '4. Insurance covers 1% of value over £1,000',
    '5. Price includes return mileage',
    '6. Quote valid for 30 days',
    '7. No hazardous materials without ADR certification',
    '8. Accurate weight declaration required',
  ];
  
  terms.forEach((term) => {
    // Check if we need a new page
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.text(term, 20, yPos);
    yPos += 5;
  });
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  
  // Check if we have space for footer
  if (yPos < 280) {
    doc.text(`${COMPANY_NAME} • ${COMPANY_PHONE} • ${COMPANY_EMAIL}`, 105, 285, { align: 'center' });
    doc.text('Hampshire, UK', 105, 290, { align: 'center' });
  } else {
    doc.addPage();
    yPos = 20;
    doc.text(`${COMPANY_NAME} • ${COMPANY_PHONE} • ${COMPANY_EMAIL}`, 105, 140, { align: 'center' });
    doc.text('Hampshire, UK', 105, 145, { align: 'center' });
  }
  
  return doc;
};

// Build the plain-text "details" block sent to the email template.
// Mirrors the PDF's sections so the emailed summary matches the download.
const buildQuoteEmailDetails = (quoteData) => {
  const lines = [
    'SERVICE DETAILS',
    `Service Type: ${quoteData.isLocal ? 'Local Hampshire' : 'UK-Wide Delivery'}`,
    `Distance (one way): ${quoteData.oneWayMiles || 0} miles`,
    `Distance (round trip): ${quoteData.roundTripMiles || 0} miles`,
    `Urgency: ${quoteData.urgency || 'Standard'}`,
    `Time Slot: ${quoteData.timeOfDay || 'Standard hours'}`,
    `Weekend Service: ${quoteData.isWeekend ? 'Yes' : 'No'}`,
    `Bank Holiday: ${quoteData.isBankHoliday ? 'Yes' : 'No'}`,
    '',
    'PICKUP',
    quoteData.pickupAddress?.street || '',
    quoteData.pickupAddress?.line2 || '',
    `${quoteData.pickupAddress?.city || ''} ${quoteData.pickupAddress?.postcode || ''}`.trim(),
    '',
    'DELIVERY',
    quoteData.deliveryAddress?.street || '',
    quoteData.deliveryAddress?.line2 || '',
    `${quoteData.deliveryAddress?.city || ''} ${quoteData.deliveryAddress?.postcode || ''}`.trim(),
    quoteData.recipient?.name ? `Recipient: ${quoteData.recipient.name}` : '',
    quoteData.recipient?.phone ? `Recipient phone: ${quoteData.recipient.phone}` : '',
    '',
    'LOAD',
    `Load Type: ${quoteData.loadType || 'Not specified'}`,
    `Number of Pallets: ${quoteData.numberOfPallets || 0}`,
    `Weight: ${quoteData.weight || 0} kg`,
    `Declared Value: ${quoteData.declaredValue ? `£${quoteData.declaredValue}` : 'Not specified'}`,
    `Waiting Time: ${quoteData.waitingTime || 0} hours`,
    '',
    'PRICE BREAKDOWN',
    `Base Rate: ${formatCurrency(quoteData.basePrice || 0)}`,
    `Urgency Surcharge: ${formatCurrency(quoteData.breakdown?.urgencySurcharge || 0)}`,
    `Time Surcharge: ${formatCurrency(quoteData.breakdown?.timeSurcharge || 0)}`,
    `Load Surcharge: ${formatCurrency(quoteData.breakdown?.loadSurcharge || 0)}`,
    `Extras: ${formatCurrency(quoteData.extrasTotal || 0)}`,
    `TOTAL: ${formatCurrency(quoteData.price || 0)}`,
  ];

  return lines.filter((line) => line !== '' || true).join('\n').replace(/\n{3,}/g, '\n\n');
};

// Sends the quote summary by email. Fire-and-forget — a failed send
// shouldn't block the PDF download, since that's already succeeded.
const sendQuoteEmail = (quoteData) => {
  const templateParams = {
    reference: quoteData.reference,
    price: formatCurrency(quoteData.price || 0),
    name: quoteData.sender?.name || '',
    phone: quoteData.sender?.phone || '',
    email: quoteData.sender?.email || '',
    details: buildQuoteEmailDetails(quoteData),
    time: new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }),
  };

  return emailjs.send(
    process.env.REACT_APP_EMAILJS_SERVICE_ID,
    process.env.REACT_APP_EMAILJS_QUOTE_TEMPLATE_ID,
    templateParams,
    process.env.REACT_APP_EMAILJS_PUBLIC_KEY
  );
};

// Main Component
const QuickQuote = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [quoteDownloaded, setQuoteDownloaded] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [reference] = useState(() => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const nums = Math.floor(1000 + Math.random() * 9000);
    const l1 = letters[Math.floor(Math.random() * letters.length)];
    const l2 = letters[Math.floor(Math.random() * letters.length)];
    return `${QUOTE_REF_PREFIX}-${l1}${l2}-${nums}`;
  });

  const cardRef = useRef(null);

  useEffect(() => {
    if (cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollBy(0, -140);
    }
  }, [activeTab]);

  // Form state
  const [formData, setFormData] = useState({
    // Pickup Details
    pickupAddress: { street: '', line2: '', postcode: '', city: '' },
    sender: { name: '', phone: '', email: '' },
    
    // Delivery Details
    deliveryAddress: { street: '', line2: '', postcode: '', city: '' },
    recipient: { name: '', phone: '' },
    
    // Service Details
    urgency: '',
    timeOfDay: '',
    isWeekend: false,
    isBankHoliday: false,
    
    // Load Details
    loadType: 'parcel',
    numberOfPallets: 0,
    weight: '',
    declaredValue: '',
    
    // Extras
    waitingTime: 0,
  });

  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [pickupValidation, setPickupValidation] = useState({ isValid: true, message: '' });

  // Urgency options (without Immediate)
  const urgencyOptions = [
    { id: 'standard', name: 'Standard Next Day', desc: 'Next working day' },
    { id: 'same_day', name: 'Same Day', desc: 'Within working hours' },
    { id: 'overnight', name: 'Overnight', desc: 'After 6pm delivery' },
  ];

  // Time slot options (without percentages)
  const timeSlots = [
    { id: 'day', name: 'Day (6am-6pm)' },
    { id: 'evening', name: 'Evening (6pm-10pm)' },
    { id: 'night', name: 'Night (10pm-6am)' },
  ];

  // Load type options (without multipliers)
  const loadTypeOptions = [
    { id: 'parcel', name: 'Small Parcels', icon: Package, desc: 'Boxes, envelopes, small items' },
    { id: 'pallet', name: 'Pallets', icon: Palette, desc: 'Euro pallets (120×80×180cm max)' },
  ];

  // Validate pickup address is in Hampshire
  const validatePickupPostcode = (postcode) => {
    if (!postcode) {
      setPickupValidation({ isValid: false, message: 'Please enter a postcode' });
      return false;
    }
    
    const isValid = isHampshirePostcode(postcode);
    if (!isValid) {
      setPickupValidation({ 
        isValid: false, 
        message: 'Pickup must be in Hampshire. We currently only service Hampshire locations.' 
      });
    } else {
      setPickupValidation({ isValid: true, message: '' });
    }
    return isValid;
  };

  // Calculate price whenever relevant fields change
  useEffect(() => {
    const calculatePriceAsync = async () => {
      if (!formData.pickupAddress.postcode || !formData.deliveryAddress.postcode || 
          !formData.urgency || !formData.loadType) {
        return;
      }

      // Validate pickup is in Hampshire
      if (!validatePickupPostcode(formData.pickupAddress.postcode)) {
        setCalculatedPrice(null);
        return;
      }

      setIsCalculating(true);
      setError(null);

      try {
        const priceData = await calculatePrice({
          fromPostcode: formData.pickupAddress.postcode,
          toPostcode: formData.deliveryAddress.postcode,
          urgency: formData.urgency,
          timeOfDay: formData.timeOfDay,
          isWeekend: formData.isWeekend,
          isBankHoliday: formData.isBankHoliday,
          loadType: formData.loadType,
          waitingHours: parseFloat(formData.waitingTime) || 0,
          declaredValue: parseFloat(formData.declaredValue) || 0,
          numberOfPallets: formData.numberOfPallets,
        });
        setCalculatedPrice(priceData);
      } catch (error) {
        setError(error.message);
        setCalculatedPrice(null);
      } finally {
        setIsCalculating(false);
      }
    };

    calculatePriceAsync();
  }, [formData]);

  // Update form data
  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // If overnight is selected and day time slot is selected, clear it
    if (field === 'urgency' && value === 'overnight' && formData.timeOfDay === 'day') {
      setFormData(prev => ({
        ...prev,
        timeOfDay: ''
      }));
    }
  };

  const updateNestedData = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  // Handle postcode input with validation
  const handlePickupPostcodeChange = (value) => {
    updateNestedData('pickupAddress', 'postcode', value);
    validatePickupPostcode(value);
  };

  // Handle numeric input
  const handleNumericChange = (field, value) => {
    // Allow empty string or numeric values
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      updateFormData(field, value);
    }
  };

  // Tab completion logic
  const isTabComplete = (tab) => {
    if (tab === 0) {
      return formData.pickupAddress.street && 
             formData.pickupAddress.postcode && 
             formData.pickupAddress.city &&
             pickupValidation.isValid;
    }
    if (tab === 1) return formData.deliveryAddress.street && formData.deliveryAddress.postcode && formData.urgency;
    if (tab === 2) return formData.loadType && formData.weight && parseFloat(formData.weight) > 0 && parseFloat(formData.weight) <= 1500;
    if (tab === 3) return formData.sender.name && formData.sender.phone && formData.sender.email;
    return true;
  };

  const tabs = ['Pickup', 'Delivery', 'Load', 'Your Details'];

  // Check if day time slot should be disabled
  const isDayTimeSlotDisabled = formData.urgency === 'overnight';

  // Handle PDF generation and download
  const handleDownloadQuote = async () => {
    const weight = parseFloat(formData.weight) || 0;
    
    // Validate weight limit
    if (weight > 1500) {
      setError('Weight cannot exceed 1,500kg (1.5 tonnes)');
      return;
    }
    
    // Validate pickup is in Hampshire
    if (!validatePickupPostcode(formData.pickupAddress.postcode)) {
      setActiveTab(0);
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.sender.email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsGenerating(true);
    setError(null);

    try {
      // Prepare quote data for PDF - ensure all values are properly formatted
      const quoteData = {
        reference,
        // Spread calculated price data
        ...calculatedPrice,
        price: calculatedPrice?.finalPrice || 0,
        basePrice: calculatedPrice?.basePrice || 0,
        oneWayMiles: calculatedPrice?.oneWayMiles || 0,
        roundTripMiles: calculatedPrice?.roundTripMiles || 0,
        isLocal: calculatedPrice?.isLocal || false,
        extrasTotal: calculatedPrice?.extrasTotal || 0,
        breakdown: {
          urgencySurcharge: calculatedPrice?.breakdown?.urgencySurcharge || 0,
          timeSurcharge: calculatedPrice?.breakdown?.timeSurcharge || 0,
          loadSurcharge: calculatedPrice?.breakdown?.loadSurcharge || 0,
        },
        // Form data
        sender: formData.sender,
        pickupAddress: formData.pickupAddress,
        deliveryAddress: formData.deliveryAddress,
        recipient: formData.recipient,
        // Load details - convert to strings
        loadType: loadTypeOptions.find(l => l.id === formData.loadType)?.name || 'Not specified',
        numberOfPallets: formData.numberOfPallets || 0,
        weight: formData.weight || '0',
        declaredValue: formData.declaredValue || '',
        waitingTime: formData.waitingTime || 0,
        // Service details - convert to strings
        urgency: urgencyOptions.find(u => u.id === formData.urgency)?.name || 'Standard',
        timeOfDay: timeSlots.find(t => t.id === formData.timeOfDay)?.name || 'Standard hours',
        isWeekend: formData.isWeekend,
        isBankHoliday: formData.isBankHoliday,
      };

      // Generate PDF
      const pdfDoc = generateQuotePDF(quoteData);
      
      // Download PDF
      pdfDoc.save(`quote-${reference}.pdf`);

      // Email the quote summary too. Don't let a failed send block the
      // success screen — the PDF already downloaded successfully.
      sendQuoteEmail(quoteData)
        .then(() => track('generate_lead', { form_name: 'Quick Quote' }))
        .catch((emailErr) => {
        console.error('Quote email failed to send:', emailErr);
      });
      
      // Show success screen
      setQuoteDownloaded(true);
    } catch (err) {
      console.error('PDF generation error:', err);
      setError(`Failed to generate quote PDF: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle download again from success screen
  const handleDownloadAgain = () => {
    const quoteData = {
      reference,
      ...calculatedPrice,
      price: calculatedPrice?.finalPrice || 0,
      basePrice: calculatedPrice?.basePrice || 0,
      oneWayMiles: calculatedPrice?.oneWayMiles || 0,
      roundTripMiles: calculatedPrice?.roundTripMiles || 0,
      isLocal: calculatedPrice?.isLocal || false,
      extrasTotal: calculatedPrice?.extrasTotal || 0,
      breakdown: {
        urgencySurcharge: calculatedPrice?.breakdown?.urgencySurcharge || 0,
        timeSurcharge: calculatedPrice?.breakdown?.timeSurcharge || 0,
        loadSurcharge: calculatedPrice?.breakdown?.loadSurcharge || 0,
      },
      sender: formData.sender,
      pickupAddress: formData.pickupAddress,
      deliveryAddress: formData.deliveryAddress,
      recipient: formData.recipient,
      loadType: loadTypeOptions.find(l => l.id === formData.loadType)?.name || 'Not specified',
      numberOfPallets: formData.numberOfPallets || 0,
      weight: formData.weight || '0',
      declaredValue: formData.declaredValue || '',
      waitingTime: formData.waitingTime || 0,
      urgency: urgencyOptions.find(u => u.id === formData.urgency)?.name || 'Standard',
      timeOfDay: timeSlots.find(t => t.id === formData.timeOfDay)?.name || 'Standard hours',
      isWeekend: formData.isWeekend,
      isBankHoliday: formData.isBankHoliday,
    };

    try {
      const pdfDoc = generateQuotePDF(quoteData);
      pdfDoc.save(`quote-${reference}.pdf`);
    } catch (err) {
      console.error('Error re-downloading PDF:', err);
      setError('Failed to download PDF. Please try again.');
    }
  };

  if (quoteDownloaded) {
    return (
      <PageWrapper>
        <Container>
          <Breadcrumb>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#001F3D' }}>
              <Home size={18} /> Home
            </Link>
            <span>›</span>
            <span style={{ color: '#1a1a1a', fontWeight: '600' }}>Get a Quick Quote</span>
          </Breadcrumb>
          <SuccessScreen 
            reference={reference} 
            price={calculatedPrice?.finalPrice || 0} 
            bookingDetails={{
              ...calculatedPrice,
              senderEmail: formData.sender.email
            }}
            onDownloadAgain={handleDownloadAgain}
          />
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <Breadcrumb>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#001F3D' }}>
            <Home size={18} /> Home
          </Link>
          <span>›</span>
          <span style={{ color: '#1a1a1a', fontWeight: '600' }}>Get a Quick Quote</span>
        </Breadcrumb>

        <Title>Get a Quick Quote</Title>

        <InfoBox style={{ marginBottom: '1.5rem' }}>
          <strong>Parcels & pallets only:</strong> this calculator covers Hampshire courier deliveries.
          Looking for a <strong>Man with a Van</strong> for a house move or item collection?{' '}
          <Link to="/man-with-van" style={{ color: '#001F3D', fontWeight: 600 }}>
            Visit our Man with a Van page
          </Link>{' '}
          instead — those jobs are quoted individually.
        </InfoBox>

        <Tabs>
          {tabs.map((label, i) => (
            <TabButton key={i} $active={activeTab === i}>
              {i < activeTab && <Check size={18} />}
              {i + 1}. {label}
            </TabButton>
          ))}
        </Tabs>

        <Card ref={cardRef} key={activeTab} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>

          {/* === TAB 0 – Pickup Details === */}
          {activeTab === 0 && (
            <>
              <SectionHeader>
                <MapPin /> Pickup Location (Hampshire Only)   
              </SectionHeader>

              {!pickupValidation.isValid && (
                <ErrorBox>
                  <AlertCircle size={20} style={{ display: 'inline', marginRight: '8px' }} />
                  {pickupValidation.message}
                </ErrorBox>
              )}

              <FormGroup>
                <Label>Pickup Street & Number <Required>*</Required></Label>
                <Input 
                  value={formData.pickupAddress.street} 
                  onChange={e => updateNestedData('pickupAddress', 'street', e.target.value)} 
                  placeholder="e.g., 123 High Street, Unit 4 Industrial Estate"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Address Line 2 (optional)</Label>
                <Input 
                  value={formData.pickupAddress.line2} 
                  onChange={e => updateNestedData('pickupAddress', 'line2', e.target.value)}
                  placeholder="Building name, suite, company name, etc."
                />
              </FormGroup>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <FormGroup>
                  <Label>Postcode <Required>*</Required></Label>
                  <Input 
                    value={formData.pickupAddress.postcode} 
                    onChange={e => handlePickupPostcodeChange(e.target.value)}
                    placeholder="e.g., SO14 3TL or PO1 3AB"
                    style={{ borderColor: pickupValidation.isValid ? '#e2e8f0' : '#f87171' }}
                  />
                  <small style={{ color: '#64748b' }}>Must be a Hampshire postcode (SO, PO, RG21-29, etc.)</small>
                </FormGroup>
                
                <FormGroup>
                  <Label>Town/City <Required>*</Required></Label>
                  <Select 
                    value={formData.pickupAddress.city} 
                    onChange={e => updateNestedData('pickupAddress', 'city', e.target.value)}
                  >
                    <option value="">Select Hampshire town</option>
                    {HAMPSHIRE_TOWNS.map(town => (
                      <option key={town} value={town}>{town}</option>
                    ))}
                    <option value="Other Hampshire">Other Hampshire Location</option>
                  </Select>
                </FormGroup>
              </div>

              <ButtonGroup>
                <div />
                <Button $primary onClick={() => setActiveTab(1)} disabled={!isTabComplete(0)}>
                  Continue <ArrowRight size={20} />
                </Button>
              </ButtonGroup>
            </>
          )}

          {/* === TAB 1 – Delivery & Service === */}
          {activeTab === 1 && (
            <>
              <SectionHeader>
                <Truck /> Delivery Address & Service
              </SectionHeader>

              <FormGroup>
                <Label>Delivery Street & Number <Required>*</Required></Label>
                <Input 
                  value={formData.deliveryAddress.street} 
                  onChange={e => updateNestedData('deliveryAddress', 'street', e.target.value)}
                  placeholder="e.g., 456 Business Park Road, Warehouse 3"
                />
              </FormGroup>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <FormGroup>
                  <Label>Delivery Postcode <Required>*</Required></Label>
                  <Input 
                    value={formData.deliveryAddress.postcode} 
                    onChange={e => updateNestedData('deliveryAddress', 'postcode', e.target.value)}
                    placeholder="e.g., M1 1AA or SO15 2DF"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Town/City (optional)</Label>
                  <Input 
                    value={formData.deliveryAddress.city} 
                    onChange={e => updateNestedData('deliveryAddress', 'city', e.target.value)}
                    placeholder="e.g., Manchester or Southampton"
                  />
                </FormGroup>
              </div>

              <FormGroup>
                <Label>Recipient Contact (optional)</Label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <Input 
                    value={formData.recipient.name} 
                    onChange={e => updateNestedData('recipient', 'name', e.target.value)}
                    placeholder="Recipient name"
                  />
                  <Input 
                    type="tel"
                    value={formData.recipient.phone} 
                    onChange={e => updateNestedData('recipient', 'phone', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>
              </FormGroup>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Select Service Level</h3>
              <UrgencyGrid>
                {urgencyOptions.map(option => {
                  const Icon = Clock; // All use same icon now
                  return (
                    <UrgencyCard
                      key={option.id}
                      $selected={formData.urgency === option.id}
                      onClick={() => updateFormData('urgency', option.id)}
                    >
                      <Icon size={32} style={{ color: '#001F3D', marginBottom: '0.5rem' }} />
                      <h4 style={{ margin: '0.5rem 0', fontSize: '1.1rem' }}>{option.name}</h4>
                      <p style={{ color: '#666', fontSize: '0.9rem', minHeight: '40px' }}>{option.desc}</p>
                    </UrgencyCard>
                  );
                })}
              </UrgencyGrid>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Preferred Collection/Delivery Time</h3>
              <TimeSlotGrid>
                {timeSlots.map(slot => (
                  <TimeSlot
                    key={slot.id}
                    $selected={formData.timeOfDay === slot.id}
                    onClick={() => !(isDayTimeSlotDisabled && slot.id === 'day') && updateFormData('timeOfDay', slot.id)}
                    $disabled={isDayTimeSlotDisabled && slot.id === 'day'}
                    style={isDayTimeSlotDisabled && slot.id === 'day' ? { 
                      opacity: 0.5, 
                      cursor: 'not-allowed',
                      backgroundColor: '#f8fafc'
                    } : {}}
                  >
                    <h4 style={{ margin: '0.5rem 0', fontSize: '1rem' }}>{slot.name}</h4>
                    {isDayTimeSlotDisabled && slot.id === 'day' && (
                      <div style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '0.25rem' }}>
                        Not available for overnight service
                      </div>
                    )}
                  </TimeSlot>
                ))}
                <TimeSlot
                  $selected={formData.isWeekend}
                  onClick={() => updateFormData('isWeekend', !formData.isWeekend)}
                >
                  <h4 style={{ margin: '0.5rem 0', fontSize: '1rem' }}>Weekend Service</h4>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.isBankHoliday}
                        onChange={(e) => updateFormData('isBankHoliday', e.target.checked)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Bank Holiday
                    </label>
                  </div>
                </TimeSlot>
              </TimeSlotGrid>

              {formData.urgency === 'overnight' && formData.timeOfDay === 'day' && (
                <InfoBox style={{ marginTop: '1rem' }}>
                  <strong>Note:</strong> Day time slot (6am-6pm) is not available for overnight service. 
                  Please select Evening or Night time slot instead.
                </InfoBox>
              )}

              <ButtonGroup>
                <Button onClick={() => setActiveTab(0)}>
                  <ArrowLeft size={20} /> Back
                </Button>
                <Button $primary onClick={() => setActiveTab(2)} disabled={!isTabComplete(1)}>
                  Continue <ArrowRight size={20} />
                </Button>
              </ButtonGroup>
            </>
          )}

          {/* === TAB 2 – Load Details === */}
          {activeTab === 2 && (
            <>
              <SectionHeader>
                <Package /> Load Details
              </SectionHeader>

              <WarningBox>
                <AlertTriangle size={20} style={{ display: 'inline', marginRight: '8px' }} />
                <strong>Maximum Payload:</strong> 1,500kg (1.5 tonnes) including vehicle, fuel, driver, and load.
              </WarningBox>

              <h3 style={{ marginBottom: '1rem' }}>Load Type</h3>
              <LoadTypeGrid>
                {loadTypeOptions.map(option => {
                  const Icon = option.icon;
                  return (
                    <LoadTypeCard
                      key={option.id}
                      $selected={formData.loadType === option.id}
                      onClick={() => {
                        updateFormData('loadType', option.id);
                        if (option.id === 'pallet') {
                          updateFormData('numberOfPallets', 1);
                        }
                      }}
                    >
                      <Icon size={24} style={{ color: '#001F3D', marginBottom: '0.5rem' }} />
                      <h4 style={{ margin: '0.5rem 0', fontSize: '1.1rem' }}>{option.name}</h4>
                      <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>{option.desc}</p>
                    </LoadTypeCard>
                  );
                })}
              </LoadTypeGrid>

              {formData.loadType === 'pallet' && (
                <FormGroup>
                  <Label>Number of Pallets</Label>
                  <Select 
                    value={formData.numberOfPallets}
                    onChange={e => updateFormData('numberOfPallets', parseInt(e.target.value) || 0)}
                  >
                    <option value="1">1 Pallet</option>
                    <option value="2">2 Pallets</option>
                    <option value="3">3 Pallets</option>
                    <option value="4">4 Pallets - Full Load</option>
                  </Select>
                  <small style={{ color: '#64748b' }}>Standard UK pallets (120×100 cm max per pallet)</small>
                </FormGroup>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '2rem' }}>
                <FormGroup>
                  <Label>Total Weight (kg) <Required>*</Required></Label>
                  <Input 
                    type="text" 
                    inputMode="decimal"
                    value={formData.weight}
                    onChange={e => handleNumericChange('weight', e.target.value)}
                    placeholder="e.g., 500"
                    style={{ borderColor: formData.weight && parseFloat(formData.weight) > 1500 ? '#f87171' : '#e2e8f0' }}
                  />
                  <small style={{ color: formData.weight && parseFloat(formData.weight) > 1500 ? '#dc2626' : '#64748b' }}>
                    Maximum: 1,500kg (1.5 tonnes)
                  </small>
                </FormGroup>
                
                <FormGroup>
                  <Label>Declared Value (£)</Label>
                  <Input 
                    type="text"
                    inputMode="decimal"
                    value={formData.declaredValue}
                    onChange={e => handleNumericChange('declaredValue', e.target.value)}
                    placeholder="Value of goods"
                  />
                  <small style={{ color: '#64748b' }}>Insurance: 1% of value over £1,000</small>
                </FormGroup>
              </div>

              <FormGroup>
                <Label>Estimated Waiting Time (hours)</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <Input 
                    type="range" 
                    min="0" 
                    max="5"
                    step="0.5"
                    value={formData.waitingTime}
                    onChange={e => updateFormData('waitingTime', parseFloat(e.target.value) || 0)}
                    style={{ flex: 1 }}
                  />
                  <div style={{ minWidth: '80px', textAlign: 'center', fontWeight: 'bold', color: '#001F3D' }}>
                    {formData.waitingTime}h
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                  <span style={{ color: '#64748b' }}>First 10 minutes free</span>
                  <span style={{ color: '#001F3D', fontWeight: 'bold' }}>£20/hour after</span>
                </div>
              </FormGroup>

              <ButtonGroup>
                <Button onClick={() => setActiveTab(1)}>
                  <ArrowLeft size={20} /> Back
                </Button>
                <Button $primary onClick={() => setActiveTab(3)} disabled={!isTabComplete(2)}>
                  Continue <ArrowRight size={20} />
                </Button>
              </ButtonGroup>
            </>
          )}

          {/* === TAB 3 – Sender Details & Quote Submission === */}
          {activeTab === 3 && (
            <>
              <SectionHeader>
                <User /> Your Contact Details
              </SectionHeader>

              <InfoBox>
                <strong>Quote Download:</strong> Enter your details below and download a detailed PDF quote immediately.
              </InfoBox>

              <FormGroup>
                <Label>Your Name <Required>*</Required></Label>
                <Input 
                  value={formData.sender.name} 
                  onChange={e => updateNestedData('sender', 'name', e.target.value)}
                  placeholder="Full name"
                />
              </FormGroup>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <FormGroup>
                  <Label>Your Phone <Required>*</Required></Label>
                  <Input 
                    type="tel"
                    value={formData.sender.phone} 
                    onChange={e => updateNestedData('sender', 'phone', e.target.value)}
                    placeholder="Mobile or landline"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Your Email <Required>*</Required></Label>
                  <Input 
                    type="email"
                    value={formData.sender.email} 
                    onChange={e => updateNestedData('sender', 'email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </FormGroup>
              </div>

              {/* Price Preview */}
              {error && (
                <ErrorBox>
                  <AlertCircle size={20} style={{ display: 'inline', marginRight: '8px' }} />
                  {error}
                </ErrorBox>
              )}

              {calculatedPrice && !isCalculating && (
                <PricingCard>
                  <h3 style={{ marginBottom: '1rem', color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    Your Quote Summary
                    {calculatedPrice.isLocal && (
                      <span style={{ fontSize: '0.9rem', background: '#dcfce7', color: '#166534', padding: '0.25rem 0.75rem', borderRadius: '20px', marginLeft: 'auto' }}>
                        Local Hampshire
                      </span>
                    )}
                  </h3>
                  
                  <PriceBreakdown>
                    <PriceRow>
                      <span>Distance (one way):</span>
                      <span>{calculatedPrice.oneWayMiles} miles</span>
                    </PriceRow>
                    <PriceRow>
                      <span>Round trip ({calculatedPrice.roundTripMiles} miles):</span>
                      <span>{calculatedPrice.roundTripMiles} miles</span>
                    </PriceRow>
                    <PriceRow>
                      <span>Base Rate ({calculatedPrice.roundTripMiles} × £1.40):</span>
                      <span>{formatCurrency(calculatedPrice.basePrice)}</span>
                    </PriceRow>
                    {calculatedPrice.urgencyMultiplier > 0 && (
                      <PriceRow>
                        <span>Urgency Surcharge:</span>
                        <span>+{formatCurrency(calculatedPrice.breakdown.urgencySurcharge)}</span>
                      </PriceRow>
                    )}
                    {calculatedPrice.timeMultiplier > 0 && (
                      <PriceRow>
                        <span>Time Surcharge:</span>
                        <span>+{formatCurrency(calculatedPrice.breakdown.timeSurcharge)}</span>
                      </PriceRow>
                    )}
                    {calculatedPrice.loadMultiplier > 0 && (
                      <PriceRow>
                        <span>Load Multiplier:</span>
                        <span>+{formatCurrency(calculatedPrice.breakdown.loadSurcharge)}</span>
                      </PriceRow>
                    )}
                    {calculatedPrice.extrasTotal > 0 && (
                      <PriceRow>
                        <span>Extras (waiting/insurance):</span>
                        <span>+{formatCurrency(calculatedPrice.extrasTotal)}</span>
                      </PriceRow>
                    )}
                    <PriceRow>
                      <span><strong>Total Quote:</strong></span>
                      <span style={{ color: '#001F3D', fontSize: '1.3rem' }}>
                        <strong>{formatCurrency(calculatedPrice.finalPrice)}</strong>
                      </span>
                    </PriceRow>
                  </PriceBreakdown>
                  
                  {calculatedPrice.basePrice < (calculatedPrice.isLocal ? 60 : 120) && (
                    <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', fontSize: '0.9rem' }}>
                      <strong>Note:</strong> Minimum charge of {formatCurrency(calculatedPrice.isLocal ? 60 : 120)} applies for {calculatedPrice.isLocal ? 'local' : 'UK-wide'} jobs.
                    </div>
                  )}
                  
                  <small style={{ color: '#64748b', display: 'block', marginTop: '1rem', fontSize: '0.85rem' }}>
                    * This is an estimate. Final price may vary based on exact requirements and traffic conditions.
                    ** Price includes return mileage.
                  </small>
                </PricingCard>
              )}

              {isCalculating && (
                <div style={{ textAlign: 'center', padding: '2rem', background: '#f8fafc', borderRadius: '12px', margin: '2rem 0' }}>
                  <LoadingSpinner style={{ display: 'inline-block', marginBottom: '1rem' }} />
                  <p style={{ color: '#64748b' }}>Calculating your quote...</p>
                </div>
              )}

              <WarningBox style={{ marginTop: '2rem' }}>
                <strong>📋 Important Information:</strong>
                <ul style={{ margin: '0.5rem 0 0 1.5rem' }}>
                  <li>Maximum payload: 1,500kg (1.5 tonnes)</li>
                  <li>All loads must be properly secured and palletized</li>
                  <li>Accurate weight declaration is required</li>
                  <li>No hazardous materials without ADR certification</li>
                  <li>First 10 minutes waiting time free, then £20/hour</li>
                  <li>Insurance covers 1% of value over £1,000</li>
                  <li>Price includes return mileage</li>
                </ul>
              </WarningBox>

              <ButtonGroup>
                <Button onClick={() => setActiveTab(2)}>
                  <ArrowLeft size={20} /> Back
                </Button>
                <Button 
                  $primary 
                  onClick={handleDownloadQuote} 
                  style={{ fontSize: '1.1rem' }}
                  disabled={!isTabComplete(3) || isCalculating || isGenerating || !calculatedPrice}
                >
                  {isGenerating ? (
                    <>
                      <LoadingSpinner /> Generating PDF...
                    </>
                  ) : (
                    <>
                      <Download size={20} /> Download PDF Quote
                    </>
                  )}
                </Button>
              </ButtonGroup>
            </>
          )}
        </Card>
      </Container>
    </PageWrapper>
  );
};

export default QuickQuote;